import { TR_PROVINCES } from './tr-address-data.gen';

/**
 * Türkiye adresleri (F5B-17, ADR-074): il ve ilçe listesi (PTT posta kodu listesinden; 81 il, 973 ilçe) ve
 * doğrulama. Sunucu ve formlar aynı kuralları kullanır:
 *  - il listeden olmalı; ilçe verildiyse o ilin ilçesi olmalı (büyük/küçük harf ve Türkçe karakter farkı yok sayılır,
 *    kayıt resmî yazımla yapılır: "istanbul" → "İstanbul", "kadikoy" → "Kadıköy");
 *  - posta kodu verildiyse 5 rakam ve ilk iki rakamı ilin plaka kodu (PTT kuralı).
 * Yalnızca Türkiye adresleri doğrulanır; yeni kayıtta uygulanır, eski kayıtlar olduğu gibi okunur.
 */
export { TR_PROVINCES };
export type TrProvince = (typeof TR_PROVINCES)[number];

/** Karşılaştırma anahtarı: Türkçe karakterler sadeleşir, büyük/küçük ve boşluk farkı kalkar. */
export function foldTr(s: string): string {
  return s
    .trim()
    .replace(/İ/g, 'i')
    .replace(/I/g, 'i')
    .toLocaleLowerCase('tr')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ı/g, 'i')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

const byFold = new Map(TR_PROVINCES.map((p) => [foldTr(p.name), p]));
const byCode = new Map(TR_PROVINCES.map((p) => [p.code, p]));

export function findProvince(name: string | null | undefined): TrProvince | null {
  return name ? (byFold.get(foldTr(name)) ?? null) : null;
}

export function provinceByCode(code: string): TrProvince | null {
  return byCode.get(code) ?? null;
}

/** İlin ilçesi (resmî yazımla) veya bulunamazsa null. */
export function findDistrict(province: TrProvince, name: string | null | undefined): string | null {
  if (!name) return null;
  const f = foldTr(name);
  return province.districts.find((d) => foldTr(d) === f) ?? null;
}

export type TrAddressIssue = {
  field: 'city' | 'district' | 'postalCode';
  code:
    'address_city_unknown' | 'address_district_unknown' | 'postal_code_format' | 'postal_code_city_mismatch';
};

/**
 * Türkiye adresini doğrular ve resmî yazıma çevirir. Hata yoksa `value` il/ilçe adları düzeltilmiş adrestir.
 * Boş ilçe ve posta kodu serbesttir (eski adresler ve ilçesini bilmeyen müşteri için).
 */
export function checkTrAddress<
  T extends { city: string; district?: string | null; postalCode?: string | null },
>(a: T): { value: T; issues: TrAddressIssue[] } {
  const issues: TrAddressIssue[] = [];
  const province = findProvince(a.city);
  if (!province) return { value: a, issues: [{ field: 'city', code: 'address_city_unknown' }] };
  let district = a.district ?? null;
  if (district && district.trim()) {
    const d = findDistrict(province, district);
    if (!d) issues.push({ field: 'district', code: 'address_district_unknown' });
    else district = d;
  }
  const postal = a.postalCode?.trim() ?? '';
  if (postal) {
    if (!/^\d{5}$/.test(postal)) issues.push({ field: 'postalCode', code: 'postal_code_format' });
    else if (postal.slice(0, 2) !== province.code)
      issues.push({ field: 'postalCode', code: 'postal_code_city_mismatch' });
  }
  return {
    value: {
      ...a,
      city: province.name,
      district: district?.trim() ? district : a.district,
      postalCode: postal || a.postalCode,
    },
    issues,
  };
}
